/* The fields a typed block is made of, and the read-only view of the same.
 *
 * Driven entirely by a kind spec — `kind.fields` — so BOTH panels use them: a
 * website section and a course block are the same idea with different specs, and
 * a field added to a spec turns up on both screens with no change here.
 *
 * The lock card lives here too. What Brill wrote waits in it until she presses
 * it in, which is rule 4 of the screen pattern, and it belongs wherever a kind's
 * fields are rendered rather than to one panel.
 */
import { useState } from "react";
import { emptyRow, fieldWords, isListField, rowsOf, wordCount } from "../../utils/websiteSectionKinds";
import { SECTION_DONE, SECTION_DRAFT, SECTION_STATUS } from "../../utils/websiteSections";
import { B, blockIcon, btnGhost, input } from "./tokens";
import { CheckCircle, Lock, Trash2 } from "lucide-react";
import { LibraryPickButton } from "./libraryPicker";
import { MediaPickButton } from "./mediaPicker";

/* ── The count, and the budget it counts against ────────────────────────────
   The industry pattern, and the reason it needs no explaining: the counter is
   always there, the number is part of the content model. Yoast on a meta
   description, a character ring on a social composer, field validation in any
   CMS — the writer sees the count and feels the constraint.

   WHAT IS DIFFERENT HERE is that she can change the number, and that is a
   stand-in rather than a feature: templates are what will set these budgets, and
   until templates exist she is the template. So the affordance is the COUNTER
   ITSELF — click the number, type another — rather than a cog, a panel or a
   settings screen. There is no permanent furniture to remove when the real
   source arrives; the template will write the same number she types.

   NEVER A LIMIT. Over budget goes amber and says so; nothing truncates, nothing
   blocks a save, and a field with no budget shows no counter at all. */
export function WordCount({ field, value, budget, onBudget }) {
  const [editing, setEditing] = useState(false);
  const max = budget ?? fieldWords(field);
  if (!max) return null;

  const n = wordCount(value);
  const over = n > max;

  const commit = (raw) => {
    setEditing(false);
    const next = parseInt(raw, 10);
    if (Number.isFinite(next) && next > 0 && next !== max) onBudget?.(next);
  };

  return (
    <span style={{ marginLeft: "auto", letterSpacing: 0, textTransform: "none", fontWeight: 600, fontSize: 11, color: over ? B.amber : B.faint, flexShrink: 0, display: "inline-flex", alignItems: "baseline", gap: 3 }}>
      <span>{n} /</span>
      {onBudget && editing ? (
        <input type="number" min="1" defaultValue={max} autoFocus aria-label={`${field.label} word budget`}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEditing(false); }}
          style={{ width: 46, padding: "0 4px", fontSize: 11, fontWeight: 600, fontFamily: "inherit", color: B.text, background: "var(--input-bg)", border: `1px solid ${B.tealLine}`, borderRadius: 5, outline: "none" }} />
      ) : onBudget ? (
        <button onClick={() => setEditing(true)} title="Change the word budget"
          style={{ padding: 0, border: "none", background: "none", font: "inherit", color: "inherit", cursor: "pointer", borderBottom: `1px dashed ${B.border2}` }}>
          {max}
        </button>
      ) : <span>{max}</span>}
      <span>words</span>
    </span>
  );
}

/* A LINK TO A PICTURE, AND THE PICTURE. The link is the field; the thumbnail
   under it is the only thing that can tell her it worked — a URL that 404s
   looks identical to one that does not until something tries to draw it, and
   the next thing that tries is a .pptx she has already sent someone.

   It loads the real image rather than validating the string, because "does
   this look like a URL" is not the question. The question is "is there a
   picture at the other end", and only the browser can answer that. */
/* A picture is a LINK: pasted, or picked from her CRM's media library (her
   choice, 2026-09-23 — "GHL library + paste"). A pick is a click with no field
   to leave afterwards, so it saves at once through the same `immediate` path a
   Library pick uses; waiting for a blur would lose it. The picker is the
   Settings logo's, reused — and the one social posts will take next. */
function ImageField({ common, value, onPick, emptyHint }) {
  const [broken, setBroken] = useState(false);
  const src = String(value || "").trim();
  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input {...common} style={{ ...input, flex: 1, minWidth: 0 }} type="url" />
        {onPick && <MediaPickButton onPick={(url) => { setBroken(false); onPick(url); }} />}
      </div>
      {/* Without a CRM the empty slot is not a dead end — it is a placeholder
          picture in the download, which Change Picture replaces. Said, so an
          empty box does not read as "this slide is broken". */}
      {!src && (
        <div style={{ fontSize: 11.5, color: B.dim, marginTop: 6, lineHeight: 1.5 }}>
          {emptyHint || "No picture yet? Leave it empty — the download carries a placeholder you can swap for your own."}
        </div>
      )}
      {src && (
        <div style={{ marginTop: 8 }}>
          {broken ? (
            <span style={{ fontSize: 12, color: B.amber }}>
              Nothing loaded from that link. It may need to be public, or it may be the page the image sits on rather than the image itself.
            </span>
          ) : (
            /* Small: this is proof, not a preview of the slide. */
            <img src={src} alt="" onError={() => setBroken(true)} onLoad={() => setBroken(false)}
              style={{ maxHeight: 96, maxWidth: "100%", borderRadius: 8, border: `1px solid ${B.border}`, display: "block" }} />
          )}
        </div>
      )}
    </div>
  );
}


/** A PICTURE ON ITS OWN, outside a kind's fields — a website section's
 *  (content.pictures, see `pictureOf`). The same box a slide's picture uses:
 *  paste a link, or Choose image (her GHL library, with Canva inside it) when
 *  a CRM is connected. A pick saves at once (`onChange(url, true)`). */
export function PictureField({ label = "Picture", guide, value, onChange, onBlur, emptyHint }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "flex", alignItems: "baseline", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>
        {label}
        {guide && <span style={{ marginLeft: 8, letterSpacing: 0, textTransform: "none", fontWeight: 500, color: B.faint, fontSize: 11 }}>{guide}</span>}
      </label>
      <ImageField
        common={{ value: value || "", onChange: (e) => onChange(e.target.value), onBlur, "aria-label": label, placeholder: "paste a link to the picture" }}
        value={value} onPick={(url) => onChange(url, true)} emptyHint={emptyHint} />
    </div>
  );
}


/** One labelled input. `line` gets an input, `prose` a textarea — the difference
 *  is a real signal about how long the answer should be, so it isn't cosmetic. */
export function SectionField({ field, value, onChange, onBlur, autoFocus, budget, onBudget }) {
  const common = {
    value: value || "",
    onChange: (e) => onChange(e.target.value),
    onBlur,
    autoFocus,
    "aria-label": field.label,
    placeholder: field.guide,
  };
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "flex", alignItems: "baseline", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>
        {field.label}
        {/* Guidance, never a limit. Nothing truncates and nothing blocks a save —
            the numbers are advice for whoever builds the page. */}
        <span style={{ marginLeft: 8, letterSpacing: 0, textTransform: "none", fontWeight: 500, color: B.faint, fontSize: 11 }}>{field.guide}</span>
        <WordCount field={field} value={value} budget={budget} onBudget={onBudget} />
      </label>
      {field.type === "prose"
        ? <textarea {...common} style={{ ...input, minHeight: 84, lineHeight: 1.65, resize: "vertical", display: "block" }} />
        : field.type === "image"
          ? <ImageField common={common} value={value} onPick={(url) => onChange(url, true)} emptyHint={field.emptyHint} />
          : <input {...common} style={input} />}
    </div>
  );
}


/** A repeating field — paragraphs, services, quotes, questions.
 *  Rows are added and removed by her; nothing here keeps a blank one around for
 *  company, because an empty row reads as something she failed to fill in. */
/* `limits` reaches each PART's counter (an FAQ answer, a service's one line),
   so it shows the same number Brill is told — a style's room or her own.
   Before 2026-10-16 the parts got no budget at all and always showed the
   kind's default, while Brill was sent whatever limits the section held. */
export function SectionListField({ field, rows, onChange, onBlur, budget, onBudget, usedAs = null, limits }) {
  const itemLabel = field.itemLabel || field.label;
  const setRow = (i, value) => onChange(rows.map((r, n) => (n === i ? value : r)));
  const setPart = (i, key, value) => setRow(i, { ...(rows[i] || {}), [key]: value });
  // Structural changes write straight through: there's no blur coming after a
  // click on Remove, so waiting for one would lose it.
  const addRow = () => onChange([...rows, emptyRow(field)], true);
  const removeRow = (i) => onChange(rows.filter((_, n) => n !== i), true);

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 8 }}>{field.label}</label>
      {rows.length === 0 && (
        <div style={{ fontSize: 12.5, color: B.faint, marginBottom: 8 }}>Nothing here yet.</div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map((row, i) => (
          <div key={i} style={{ border: `1px solid ${B.border}`, borderRadius: 10, padding: "12px 14px 2px", background: B.bg }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: B.dim }}>{itemLabel} {i + 1}</span>
              <button onClick={() => removeRow(i)} title={`Remove ${itemLabel.toLowerCase()} ${i + 1}`} style={blockIcon}><Trash2 size={13} /></button>
            </div>
            {field.parts
              ? field.parts.map((p) => (
                  <SectionField key={p.key} field={p} value={row?.[p.key]} onChange={(v) => setPart(i, p.key, v)} onBlur={onBlur} budget={fieldWords(p, limits)} />
                ))
              : <SectionField field={{ ...field, label: itemLabel, type: "prose" }} value={row} onChange={(v) => setRow(i, v)} onBlur={onBlur} budget={budget} onBudget={onBudget} />}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
        <button onClick={addRow} style={{ ...btnGhost, padding: "7px 13px", fontSize: 12 }}>
          + Add {itemLabel.toLowerCase()}
        </button>
        {/* Only where the kind says this list can be filled from the Library,
            and only where the caller said what the pick is used BY — without
            that the Library could not record where the quote went. */}
        {field.library && usedAs && (
          <LibraryPickButton kinds={[field.library.kind]} requirePermission={field.library.requirePermission}
            usedAs={usedAs} onPick={(item) => onChange([...rows, field.library.row(item)], true)} />
        )}
      </div>
    </div>
  );
}


/** Every field of a kind, in order. Shared by the section card and the drafting
 *  screen so the fields she edits are literally the same fields either way. */
/**
 * @param limits   her per-field overrides for this section, if any
 * @param onLimits (key, words) => void. Absent means the numbers are READ ONLY —
 *                 which is right wherever a section row is not the thing being
 *                 edited, and is why the counter degrades to plain text rather
 *                 than offering an edit that goes nowhere.
 */
export function SectionFields({ kind, content, onChange, onBlur, limits, onLimits, usedAs = null }) {
  const set = (key, value, immediate) => onChange({ ...content, [key]: value }, immediate);
  return (
    <>
      {/* A KIND-level pick fills several fields from one item — a quote slide
          takes the words and who said them. Word for word; stories and
          objections are context for Brill instead, which is the chat's job. */}
      {kind.library && usedAs && (
        <div style={{ marginBottom: 14 }}>
          <LibraryPickButton kinds={[kind.library.kind]} requirePermission={kind.library.requirePermission}
            usedAs={usedAs} onPick={(item) => onChange({ ...content, ...kind.library.fill(item) }, true)} />
        </div>
      )}
      {kind.fields.map((f) => (isListField(f)
        ? <SectionListField key={f.key} field={f} rows={rowsOf(content, f)} onChange={(rows, immediate) => set(f.key, rows, immediate)} onBlur={onBlur}
            budget={fieldWords(f, limits)} onBudget={onLimits && ((n) => onLimits(f.key, n))} usedAs={usedAs} limits={limits} />
        : <SectionField key={f.key} field={f} value={content?.[f.key]} onChange={(v, immediate) => set(f.key, v, immediate)} onBlur={onBlur}
            budget={fieldWords(f, limits)} onBudget={onLimits && ((n) => onLimits(f.key, n))} />
      ))}
    </>
  );
}


/** The Draft / Done switch on a section. Hers to set — nothing promotes a
 *  section on her behalf, including Brill writing into it. */
/* THE PILL CARRIES THE STATE IN ITS COLOUR. Both selected states used to be
   teal, which meant a section sitting on Draft wore the same green as one she
   had marked Done — and teal is the colour every dot on every rail uses for
   "finished". She asked for it in those words on 2026-09-15: "draft with a
   yellow outline or done green."

   That is rule 5 doing what it says (amber is attention, teal is the finished
   thing) and it is half of why "what turns it green" had no answer: the control
   that sets the state was not showing it.

   NOTHING AUTOSETS THIS. Her call 6.1: written is Draft, Done is her mark. The
   fields save themselves as she leaves them — that is rule 1 and it has not
   changed — but saving is not finishing, and this is the one thing on the
   screen she sets and nothing sets for her. */
export function SectionStatus({ value, onChange }) {
  const current = value || SECTION_DRAFT;
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {SECTION_STATUS.map(([val, label]) => {
        const on = current === val;
        const tone = val === SECTION_DONE
          ? { line: B.teal, fill: B.tealQuiet, ink: B.teal }
          : { line: B.amberLine, fill: B.amberQuiet, ink: B.amber };
        return (
          <button key={val} onClick={() => onChange(val)}
            aria-pressed={on} aria-label={label} style={{
              padding: "4px 11px", borderRadius: 16, fontSize: 11.5, fontFamily: "inherit", cursor: "pointer",
              fontWeight: on ? 700 : 600,
              border: `1px solid ${on ? tone.line : B.border2}`,
              background: on ? tone.fill : "transparent",
              color: on ? tone.ink : B.faint,
            }}>{label}</button>
        );
      })}
    </div>
  );
}


/* One row of a section's copy, read-only. Shared by the open section and by
   Brill's suggestion card, so what she is offered looks like what she'll get.

   A row of a parts list (a service, say: name, price, blurb) is joined with a
   middot rather than JSON.stringify'd, which is what the first version did and
   which put `{"name":"..."}` on screen. */
export function rowText(r) {
  if (typeof r === "string") return r;
  return Object.values(r || {}).map((v) => String(v || "").trim()).filter(Boolean).join(" · ");
}


export function SectionRead({ kind, content, gap = 15 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap, maxWidth: "66ch" }}>
      {kind.fields.map((f) => {
        const rows = isListField(f) ? rowsOf(content, f) : null;
        const value = rows ? rows.map(rowText).filter(Boolean) : String(content?.[f.key] || "").trim();
        /* A PICTURE IS NEVER SKIPPED. Her report, 2026-09-23: she added an
           Image + Text slide, opened it, and saw no sign it took a picture at
           all — reading mode drops empty fields, and an empty picture is the one
           a reader cannot tell is missing. So it shows, or says it is absent and
           where to add one. */
        if (f.type === "image") {
          // An OPTIONAL picture is drawn only when chosen, so empty it is not
          // missing — "No picture yet" would read as a gap to fill.
          if (!value && f.optional) return null;
          return (
            <div key={f.key}>
              <div style={{ marginBottom: 5, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: B.faint }}>{f.label}</div>
              {value
                ? <img src={value} alt="" style={{ display: "block", maxHeight: 120, maxWidth: "100%", borderRadius: 8, border: `1px solid ${B.border}` }} />
                : <div style={{ fontSize: 13, color: B.dim }}>No picture yet — press the pencil to choose or upload one.</div>}
            </div>
          );
        }
        if (!value || (rows && !value.length)) return null;
        return (
          <div key={f.key}>
            <div style={{ marginBottom: 5, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: B.faint }}>{f.label}</div>
            {rows
              ? <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.7, color: B.text }}>{value.map((r, i) => <li key={i}>{r}</li>)}</ul>
              : <div style={{ fontSize: 13.5, lineHeight: 1.7, color: B.text, whiteSpace: "pre-wrap" }}>{value}</div>}
          </div>
        );
      })}
    </div>
  );
}


/* What Brill wrote, waiting on her word.
 *
 * This screen used to drop Brill's draft straight into the fields and tell her
 * it was there. That reads as Brill deciding. It's the same call the Accelerator
 * already made in `0bdb08d` — a lock is her word, not Brill's — so the copy now
 * sits here until she presses it in, and the button says where it lands rather
 * than leaving "suggested copy" to imply it.
 *
 * Unlike the Accelerator's version, typing does NOT dismiss this. There the copy
 * was in the message above and survived; here the card is the only place the
 * draft exists — it's stripped out of the chat text and isn't in the fields yet
 * — so clearing it on a keystroke would be throwing her draft away. It's
 * replaced when the next one arrives, and nothing else touches it. */
/* `parts` + `target`: one card for a reply that fills SEVERAL blocks at once —
   the whole-module Course chat. Each part is read under its own name, and the
   button names the record rather than one block. Without them it is the
   one-block card it always was. */
export function SuggestedCopy({ kind, content, onLock, onDismiss, parts = null, target = null }) {
  return (
    <div style={{ border: `1px dashed ${B.tealLine}`, background: B.tealQuiet, borderRadius: 12, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: B.teal, marginBottom: 9 }}>
        Suggested copy
      </div>
      {parts ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {parts.map((p) => (
            <div key={p.kind.id}>
              <div style={{ fontSize: 12, fontWeight: 700, color: B.text, marginBottom: 6 }}>{p.kind.label}</div>
              <SectionRead kind={p.kind} content={p.content} gap={11} />
            </div>
          ))}
        </div>
      ) : (
        <SectionRead kind={kind} content={content} gap={11} />
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <button onClick={onLock}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 13px", borderRadius: 7, border: "none", background: "var(--btn-bg)", color: "var(--btn-text)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          <CheckCircle size={11} /> Lock it into {target || `the ${kind.label.toLowerCase()}`}
        </button>
        <button onClick={onDismiss}
          style={{ padding: "6px 13px", borderRadius: 7, border: `1px solid ${B.border2}`, background: B.surface, color: B.mid, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Keep chatting
        </button>
      </div>
    </div>
  );
}

